/** Reactive app state (Preact signals). Components read .value and re-render. */
import { computed, signal } from "@preact/signals";
import type { EmlPreview, Envelope, FullMessage, Mailbox, RemoteContentDefault } from "../shared/types";

export type View = "loading" | "login" | "app";
export type MobilePane = "sidebar" | "list" | "reader";

export const view = signal<View>("loading");

/* auth */
export const userEmail = signal("");
export const isAdmin = signal(false);
export const authError = signal<string | null>(null);
export const signingIn = signal(false);
export const defaultDomain = signal("");
/** True when the self-service password-change feature flag is enabled server-side. */
export const passwordChangeEnabled = signal(false);
/**
 * New-password policy bounds, hydrated from the server session so the client
 * never hardcodes the real policy. Values below are pre-hydration placeholders
 * (mirroring the server defaults); the live bounds arrive with SessionInfo.
 */
export const passwordMinLength = signal(12);
export const passwordMaxLength = signal(64);
/**
 * The account's remote-content default, hydrated from SessionInfo. Drives the
 * Reader's "Remote content was blocked" banner (suppressed when `always`) and
 * the Settings Privacy toggle. Pre-hydration placeholder is the safe `block`.
 */
export const remoteContentDefault = signal<RemoteContentDefault>("block");

/* mailboxes */
export const mailboxes = signal<Mailbox[]>([]);
export const currentMailbox = signal("INBOX");

/* message list */
export const messages = signal<Envelope[]>([]);
export const messagesTotal = signal(0);
export const page = signal(1);
export const listLoading = signal(false);
export const listError = signal<string | null>(null);
/** True while fetching the next page for infinite scroll (list already has items). */
export const loadingMore = signal(false);

/* selection / filters */
export const selectedUid = signal<number | null>(null);
export const selectedUids = signal<Set<number>>(new Set());
export const onlyUnread = signal(false);

/* derived */
/** True if more pages exist beyond what's currently loaded (drives infinite scroll). */
export const hasMoreMessages = computed(() => messages.value.length < messagesTotal.value);

export const currentMailboxObj = computed(
  () => mailboxes.value.find((m) => m.path === currentMailbox.value) ?? null,
);

export const visibleMessages = computed(() =>
  onlyUnread.value ? messages.value.filter((m) => !m.seen) : messages.value,
);

export const selectedMessage = computed(
  () => messages.value.find((m) => m.uid === selectedUid.value) ?? null,
);

/* open message (reading pane) */
export const fullMessage = signal<FullMessage | null>(null);
export const messageLoading = signal(false);
export const messageError = signal<string | null>(null);
export const showRemote = signal(false);

/* nested .eml attachment preview (modal) */
/** Parsed preview of the currently-open `.eml` attachment, or null when closed. */
export const emlPreview = signal<EmlPreview | null>(null);
/** True while the preview request is in flight. */
export const emlPreviewLoading = signal(false);
/** User-facing error message when a preview cannot be shown, else null. */
export const emlPreviewError = signal<string | null>(null);

/* composer */
export type ComposeMode = "new" | "reply" | "replyAll" | "forward";

export interface ComposerDraft {
  mode: ComposeMode;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  /** Threading headers carried from the message being replied to. */
  inReplyTo?: string;
  references?: string[];
  /** Whether the Cc/Bcc row is expanded in the UI. */
  showCc: boolean;
}

export const composer = signal<ComposerDraft | null>(null);
export const composerSending = signal(false);
export const composerError = signal<string | null>(null);
export const composerNotice = signal<string | null>(null);

/* draft persistence */
/** Snapshot of the composer state at the time it was opened / last reset. */
export const composerInitial = signal<ComposerDraft | null>(null);
/** IMAP UID of the most recently saved draft in the Drafts mailbox. */
export const composerDraftUid = signal<number | null>(null);
/** True while an autosave request is in flight. */
export const composerSaving = signal(false);
/** Snapshot of the composer state at the time of the last successful save. */
export const composerLastSaved = signal<ComposerDraft | null>(null);
/** True when the current composer has unsaved changes (deep compare). */
export const composerDirty = computed(() => {
  const cur = composer.value;
  const init = composerInitial.value;
  if (cur === null && init === null) return false;
  if (cur === null || init === null) return true;
  return JSON.stringify(cur) !== JSON.stringify(init);
});
/** True when the confirmation dialog should be shown. */
export const composerShowConfirm = signal(false);

/* folder management */
export const folderEditPath = signal<string | null>(null);
export const folderCreateParent = signal<string | null | undefined>(undefined);
// undefined = closed, null = root level, string = nested under this path
export const folderActionError = signal<string | null>(null);

/* search */
export const searchQuery = signal("");
export const searchResults = signal<Envelope[] | null>(null);
export const searchLoading = signal(false);
export const searchError = signal<string | null>(null);
export const isSearching = computed(() => searchResults.value !== null);

/* keyboard / modal */
export const showShortcuts = signal(false);
export const showSettings = signal(false);

/* mobile navigation */
export const mobilePane = signal<MobilePane>("list");
